import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
    User,
    ArrowUp,
    Wifi,
    Utensils,
    DoorOpen,
    BellRing,
    Sparkles,
    CloudMoon,
    Loader2,
    ArrowLeft,
    ThumbsUp,
    ThumbsDown,
    Sun,
    Moon,
    CloudSun,
    CloudFog,
    CloudDrizzle,
    CloudRain,
    CloudSnow,
    CloudLightning,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Mic,
    Square,
    X
} from 'lucide-react';

const Typewriter = ({ text, minSpeed = 30, maxSpeed = 90 }) => {
    const [displayedText, setDisplayedText] = useState('');

    useEffect(() => {
        setDisplayedText('');
        let i = 0;
        let timeoutId;

        const typeNextChar = () => {
            if (i < text.length) {
                setDisplayedText(text.substring(0, i + 1));
                i++;
                const randomDelay = Math.floor(Math.random() * (maxSpeed - minSpeed + 1) + minSpeed);
                timeoutId = setTimeout(typeNextChar, randomDelay);
            }
        };

        timeoutId = setTimeout(typeNextChar, 100);

        return () => clearTimeout(timeoutId);
    }, [text, minSpeed, maxSpeed]);

    return <span>{displayedText}</span>;
};

const VOICE_WAVE_BAR_COUNT = 26;
const VOICE_WAVE_MIN_HEIGHT = 6;
const VOICE_WAVE_IDLE_HEIGHT = 10;
const VOICE_WAVE_MAX_HEIGHT = 34;
const VOICE_ACTIVITY_THRESHOLD = 0.018;

const createIdleVoiceLevels = () => Array.from(
    { length: VOICE_WAVE_BAR_COUNT },
    () => VOICE_WAVE_IDLE_HEIGHT
);

const ImageCarousel = ({ images }) => {
    const trackRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const scrollTo = (index) => {
        if (!trackRef.current) return;
        const slides = trackRef.current.children;
        if (slides[index]) {
            slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    const handleScroll = () => {
        if (!trackRef.current) return;
        const track = trackRef.current;
        const slideWidth = track.offsetWidth;
        const newIndex = Math.round(track.scrollLeft / slideWidth);
        setActiveIndex(newIndex);
    };

    const goPrev = () => { if (activeIndex > 0) { scrollTo(activeIndex - 1); setActiveIndex(activeIndex - 1); } };
    const goNext = () => { if (activeIndex < images.length - 1) { scrollTo(activeIndex + 1); setActiveIndex(activeIndex + 1); } };

    return (
        <div className="mt-3 image-carousel-enter relative">
            {/* Carousel Track */}
            <div
                ref={trackRef}
                className="carousel-track"
                onScroll={handleScroll}
            >
                {images.map((imgUrl, i) => (
                    <div key={i} className="carousel-slide">
                        <img src={imgUrl} alt={`Reference ${i + 1}`} />
                        <div className="carousel-overlay" />
                        <a
                            href={imgUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-black/60 transition-all z-10"
                            title="Open full size"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    </div>
                ))}
            </div>

            {/* Navigation Arrows (only for multiple images) */}
            {images.length > 1 && (
                <>
                    {activeIndex > 0 && (
                        <button onClick={goPrev} className="carousel-nav-btn" style={{ left: 6 }}>
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                    )}
                    {activeIndex < images.length - 1 && (
                        <button onClick={goNext} className="carousel-nav-btn" style={{ right: 6 }}>
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    )}

                    {/* Dot Indicators */}
                    <div className="flex items-center justify-center gap-1.5 mt-2">
                        {images.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => { scrollTo(i); setActiveIndex(i); }}
                                className={`carousel-dot ${i === activeIndex ? 'carousel-dot-active' : ''}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default function ChatInterface({
    messages,
    loading,
    onSendMessage,
    onSendFeedback,
    hotelLogoPath,
    displayedHotelName,
    suggestions,
    onBack,
    guestName,
    onLoadMore,
    hasMoreHistory,
    isLoadingHistory
}) {
    const [input, setInput] = useState('');
    const [weather, setWeather] = useState(null);
    const scrollAreaRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [loadingPhase, setLoadingPhase] = useState('idle'); // 'idle' | 'waiting' | 'thinking' | 'typing'
    const [voiceState, setVoiceState] = useState('idle'); // 'idle' | 'requesting' | 'recording' | 'transcribing'
    const [voiceError, setVoiceError] = useState('');
    const [voiceLevels, setVoiceLevels] = useState(() => createIdleVoiceLevels());
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const discardRecordingRef = useRef(false);
    const transcriptionAbortRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const waveformFrameRef = useRef(null);
    const waveformDataRef = useRef(null);
    const waveformSlotLevelsRef = useRef(createIdleVoiceLevels());
    const waveformStepRef = useRef(0);
    const lastWaveformUpdateRef = useRef(0);

    const BACKGROUND_IMAGE_URL = 'https://i.postimg.cc/nhPDPjqg/Chat-GPT-Image-25-Noe-2025-10-42-19-p-m.png?q=80&w=1000&auto=format&fit=crop';

    function stopVoiceAnalyzer() {
        if (waveformFrameRef.current) {
            cancelAnimationFrame(waveformFrameRef.current);
            waveformFrameRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => { });
            audioContextRef.current = null;
        }

        analyserRef.current = null;
        waveformDataRef.current = null;
        waveformSlotLevelsRef.current = createIdleVoiceLevels();
        waveformStepRef.current = 0;
        lastWaveformUpdateRef.current = 0;
    }

    function getCurrentVoiceVolume(analyser, dataArray) {
        analyser.getByteTimeDomainData(dataArray);

        let sum = 0;
        for (let sampleIndex = 0; sampleIndex < dataArray.length; sampleIndex += 1) {
            const value = (dataArray[sampleIndex] - 128) / 128;
            sum += value * value;
        }

        return Math.sqrt(sum / dataArray.length);
    }

    function getIncomingVoiceHeight(rms, step) {
        if (rms <= VOICE_ACTIVITY_THRESHOLD) {
            return step % 6 === 0 ? VOICE_WAVE_IDLE_HEIGHT + 4 : VOICE_WAVE_IDLE_HEIGHT;
        }

        const normalizedLevel = Math.min(1, Math.pow(rms * 9.5, 0.68));
        const voiceTexture = 0.84 + Math.sin(step * 1.7) * 0.10 + Math.sin(step * 0.47) * 0.06;
        return VOICE_WAVE_MIN_HEIGHT
            + normalizedLevel * (VOICE_WAVE_MAX_HEIGHT - VOICE_WAVE_MIN_HEIGHT) * voiceTexture;
    }

    function getLiveVoiceLevels(analyser, dataArray) {
        const rms = getCurrentVoiceVolume(analyser, dataArray);
        const existingLevels = waveformSlotLevelsRef.current.length === VOICE_WAVE_BAR_COUNT
            ? waveformSlotLevelsRef.current
            : createIdleVoiceLevels();
        const step = waveformStepRef.current;
        const shiftedLevels = existingLevels.slice(1);
        const incomingHeight = getIncomingVoiceHeight(rms, step);
        const nextLevels = [...shiftedLevels, Math.min(VOICE_WAVE_MAX_HEIGHT, incomingHeight)];

        waveformStepRef.current = step + 1;
        waveformSlotLevelsRef.current = nextLevels;
        return nextLevels;
    }
    function startVoiceAnalyzer(stream) {
        stopVoiceAnalyzer();

        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextConstructor) {
            waveformSlotLevelsRef.current = createIdleVoiceLevels();
            waveformStepRef.current = 0;
            setVoiceLevels(waveformSlotLevelsRef.current);
            return;
        }

        const audioContext = new AudioContextConstructor();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.72;

        const dataArray = new Uint8Array(analyser.fftSize);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        waveformDataRef.current = dataArray;
        waveformSlotLevelsRef.current = createIdleVoiceLevels();
        waveformStepRef.current = 0;
        setVoiceLevels(waveformSlotLevelsRef.current);

        audioContext.resume().catch(() => { });

        const updateWaveform = (timestamp) => {
            if (timestamp - lastWaveformUpdateRef.current > 70) {
                setVoiceLevels(getLiveVoiceLevels(analyser, dataArray));
                lastWaveformUpdateRef.current = timestamp;
            }

            waveformFrameRef.current = requestAnimationFrame(updateWaveform);
        };

        waveformFrameRef.current = requestAnimationFrame(updateWaveform);
    }

    function stopAudioStream() {
        stopVoiceAnalyzer();

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    }

    function getSupportedAudioMimeType() {
        if (!window.MediaRecorder) return '';

        const mimeTypes = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/ogg;codecs=opus',
            'audio/wav'
        ];

        return mimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
    }

    function getAudioFileExtension(mimeType) {
        if (mimeType.includes('mp4')) return 'mp4';
        if (mimeType.includes('mpeg')) return 'mp3';
        if (mimeType.includes('ogg')) return 'ogg';
        if (mimeType.includes('wav')) return 'wav';
        return 'webm';
    }

    function getSpeechToTextEndpoints() {
        if (process.env.REACT_APP_SPEECH_TO_TEXT_ENDPOINT) {
            return [process.env.REACT_APP_SPEECH_TO_TEXT_ENDPOINT];
        }

        if (!process.env.REACT_APP_API_BASE) return [];

        const apiBase = process.env.REACT_APP_API_BASE.replace(/\/$/, '');
        return [`${apiBase}/speech-to-text`];
    }

    function getConfiguredTranscriptionLanguage() {
        const language = process.env.REACT_APP_TRANSCRIPTION_LANGUAGE;
        if (!language) return '';

        return language.split(/[-_]/)[0].trim().toLowerCase();
    }

    function getEndpointLabel(endpoint) {
        try {
            return new URL(endpoint, window.location.origin).pathname;
        } catch (_) {
            return endpoint;
        }
    }

    async function getTranscriptFromResponse(response) {
        const contentType = response.headers.get('content-type') || '';

        if (!contentType.includes('application/json')) {
            return response.text();
        }

        const data = await response.json();
        return data.text || data.transcript || data.transcription || data.data?.text || '';
    }

    async function getErrorMessageFromResponse(response) {
        const contentType = response.headers.get('content-type') || '';

        try {
            if (contentType.includes('application/json')) {
                const data = await response.json();
                return data.error?.message || data.error || data.message || data.details || '';
            }

            return response.text();
        } catch (_) {
            return '';
        }
    }

    function createTranscriptionError(response, endpoint, message) {
        const endpointLabel = getEndpointLabel(endpoint);
        const error = new Error(message || `Speech-to-text failed with status ${response.status} at ${endpointLabel}.`);
        error.status = response.status;
        error.endpoint = endpointLabel;
        return error;
    }

    function getVoiceErrorMessage(error) {
        if (error.status === 404 || error.status === 405) {
            return 'Speech-to-text is not set up on the API yet.';
        }
        if (error.status === 401 || error.status === 403) {
            return 'Speech-to-text is not authorized on the API.';
        }
        if (error.status === 413) {
            return 'That recording is too large. Please try a shorter message.';
        }
        if (error.message) {
            return `Could not transcribe audio: ${error.message.slice(0, 140)}`;
        }

        return 'Could not transcribe that audio. Please try again.';
    }

    async function transcribeAudio(audioBlob, mimeType, signal) {
        const endpoints = getSpeechToTextEndpoints();

        if (!endpoints.length) {
            throw new Error('Missing speech-to-text API endpoint.');
        }

        const extension = getAudioFileExtension(mimeType || audioBlob.type || 'audio/webm');
        const audioFile = new File([audioBlob], `voice-message.${extension}`, {
            type: mimeType || audioBlob.type || 'audio/webm'
        });
        const headers = {};

        if (process.env.REACT_APP_WEBHOOK_SECRET) {
            headers['x-webhook-secret'] = process.env.REACT_APP_WEBHOOK_SECRET;
        }

        let lastError;

        for (let index = 0; index < endpoints.length; index += 1) {
            const endpoint = endpoints[index];
            const formData = new FormData();
            const transcriptionLanguage = getConfiguredTranscriptionLanguage();
            formData.append('file', audioFile);
            formData.append('model', process.env.REACT_APP_TRANSCRIPTION_MODEL || 'whisper-1');
            formData.append('response_format', 'json');
            if (transcriptionLanguage) {
                formData.append('language', transcriptionLanguage);
            }

            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers,
                    body: formData,
                    signal
                });

                if (!response.ok) {
                    const errorText = await getErrorMessageFromResponse(response);
                    lastError = createTranscriptionError(response, endpoint, errorText);

                    if ((response.status === 404 || response.status === 405) && index < endpoints.length - 1) {
                        continue;
                    }

                    throw lastError;
                }

                const transcript = (await getTranscriptFromResponse(response)).trim();

                if (!transcript) {
                    throw new Error('No speech was detected.');
                }

                return transcript;
            } catch (error) {
                if (error.name === 'AbortError') throw error;
                lastError = error;
                if (index === endpoints.length - 1) throw lastError;
            }
        }

        throw lastError || new Error('Speech-to-text failed.');
    }

    const startVoiceRecording = async () => {
        if (loading || voiceState !== 'idle') return;

        setVoiceError('');
        discardRecordingRef.current = false;

        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setVoiceError('Voice recording is not supported in this browser.');
            return;
        }

        setVoiceState('requesting');

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true
                }
            });

            if (discardRecordingRef.current) {
                stream.getTracks().forEach((track) => track.stop());
                setVoiceState('idle');
                return;
            }

            const mimeType = getSupportedAudioMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

            streamRef.current = stream;
            audioChunksRef.current = [];
            mediaRecorderRef.current = recorder;
            startVoiceAnalyzer(stream);

            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = async () => {
                const recordedMimeType = recorder.mimeType || mimeType || 'audio/webm';
                const audioBlob = new Blob(audioChunksRef.current, { type: recordedMimeType });
                const shouldDiscardRecording = discardRecordingRef.current;

                stopAudioStream();
                mediaRecorderRef.current = null;

                if (shouldDiscardRecording) {
                    discardRecordingRef.current = false;
                    audioChunksRef.current = [];
                    setVoiceState('idle');
                    return;
                }

                if (!audioBlob.size) {
                    setVoiceState('idle');
                    setVoiceError('No audio was captured.');
                    return;
                }

                setVoiceState('transcribing');
                const abortController = new AbortController();
                transcriptionAbortRef.current = abortController;

                try {
                    const transcript = await transcribeAudio(audioBlob, recordedMimeType, abortController.signal);
                    setInput((currentInput) => {
                        const trimmedInput = currentInput.trim();
                        return trimmedInput ? `${trimmedInput} ${transcript}` : transcript;
                    });
                } catch (error) {
                    if (error.name !== 'AbortError') {
                        console.error('Speech-to-text error:', error);
                        setVoiceError(getVoiceErrorMessage(error));
                    }
                } finally {
                    if (transcriptionAbortRef.current === abortController) {
                        transcriptionAbortRef.current = null;
                    }
                    discardRecordingRef.current = false;
                    setVoiceState('idle');
                    audioChunksRef.current = [];
                }
            };

            recorder.onerror = () => {
                discardRecordingRef.current = false;
                stopAudioStream();
                setVoiceState('idle');
                setVoiceError('Recording stopped unexpectedly.');
            };

            recorder.start();

            setVoiceState('recording');
        } catch (error) {
            console.error('Microphone error:', error);
            stopAudioStream();
            setVoiceState('idle');
            setVoiceError(error.name === 'NotAllowedError'
                ? 'Microphone permission was denied.'
                : 'Could not start voice recording.');
        }
    };

    const stopVoiceRecording = () => {
        discardRecordingRef.current = false;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            return;
        }

        stopAudioStream();
        setVoiceState('idle');
    };

    const cancelVoiceInteraction = () => {
        setVoiceError('');
        discardRecordingRef.current = true;

        if (transcriptionAbortRef.current) {
            transcriptionAbortRef.current.abort();
            transcriptionAbortRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            return;
        }

        stopAudioStream();
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        setVoiceState('idle');
    };

    const handleVoiceButtonClick = () => {
        if (voiceState === 'recording') {
            stopVoiceRecording();
            return;
        }

        startVoiceRecording();
    };

    useEffect(() => {
        return () => {
            discardRecordingRef.current = true;
            if (transcriptionAbortRef.current) {
                transcriptionAbortRef.current.abort();
            }
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.onstop = null;
                mediaRecorderRef.current.stop();
            }

            if (waveformFrameRef.current) {
                cancelAnimationFrame(waveformFrameRef.current);
                waveformFrameRef.current = null;
            }
            if (audioContextRef.current) {
                audioContextRef.current.close().catch(() => { });
                audioContextRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        };
    }, []);

    // Preload background image
    useEffect(() => {
        const img = new Image();
        img.src = BACKGROUND_IMAGE_URL;
        img.onload = () => setImageLoaded(true);
        img.onerror = () => setImageLoaded(true); // Show UI even if image fails
    }, []);

    // Scroll to bottom when messages change, except when loading older history via scroll-up
    const scrollPositionRef = useRef(null);
    const prevMessagesRef = useRef(messages);
    const hasMounted = useRef(false);

    // useLayoutEffect runs BEFORE browser paint — prevents blink on history prepend
    useLayoutEffect(() => {
        if (!scrollAreaRef.current) return;

        const prevMessages = prevMessagesRef.current;
        const lastMsgChanged = messages.length > 0 && prevMessages.length > 0 &&
            messages[messages.length - 1].content !== prevMessages[prevMessages.length - 1].content;

        const isInitialLoad = prevMessages.length === 1 && messages.length > 1;
        const messagesPrepended = messages.length > prevMessages.length &&
            messages[messages.length - 1] === prevMessages[prevMessages.length - 1];
        const messageAppended = messages.length > prevMessages.length &&
            messages[0] === prevMessages[0];
        const initialMount = !hasMounted.current;

        if (scrollPositionRef.current !== null || (messagesPrepended && !messageAppended)) {
            // Restore scroll position for history load
            if (scrollPositionRef.current !== null) {
                const newScrollHeight = scrollAreaRef.current.scrollHeight;
                scrollAreaRef.current.scrollTop = newScrollHeight - scrollPositionRef.current;

                if (!isLoadingHistory) {
                    scrollPositionRef.current = null;
                }
            }
        } else if (initialMount || messageAppended || lastMsgChanged || isInitialLoad || messages.length <= 1) {
            // Initial mount, new message, initial visit, or STREAMING update — scroll to bottom
            scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }

        prevMessagesRef.current = messages;
        hasMounted.current = true;
    }, [messages, loading, loadingPhase, imageLoaded, isLoadingHistory]);

    // Detect scroll to top for loading more history
    const handleScroll = () => {
        if (!scrollAreaRef.current || !hasMoreHistory || isLoadingHistory) return;
        if (scrollAreaRef.current.scrollTop < 50) {
            // Save current scroll height before prepend
            scrollPositionRef.current = scrollAreaRef.current.scrollHeight;
            onLoadMore();
        }
    };



    useEffect(() => {
        let timer1, timer2;
        if (loading) {
            setLoadingPhase('waiting');
            timer1 = setTimeout(() => setLoadingPhase('thinking'), 500);
            timer2 = setTimeout(() => setLoadingPhase('typing'), 1000);
        } else {
            setLoadingPhase('idle');
        }
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, [loading]);

    // Fetch Weather
    useEffect(() => {
        async function fetchWeather() {
            try {
                const lat = localStorage.getItem('hotelLatitude') || '37.971630610096454';
                const lon = localStorage.getItem('hotelLongitude') || '23.72651282531025';
                const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,is_day`);
                const data = await res.json();
                setWeather(data.current);
            } catch (e) {
                console.error('Weather fetch error:', e);
            }
        }
        fetchWeather();
    }, []);

    const handleSend = (e) => {
        if (e) e.preventDefault();
        if (input.trim() && !loading && voiceState === 'idle') {
            onSendMessage(input);
            setInput('');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSuggestionClick = (text) => {
        if (!loading && voiceState === 'idle') {
            onSendMessage(text);
        }
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) return 'Good Morning';
        if (hour >= 12 && hour < 18) return 'Good Afternoon';
        return 'Good Evening';
    };

    const getWeatherIcon = (code, isDay) => {
        // WMO Weather interpretation codes (WW)
        // 0: Clear sky
        if (code === 0) return isDay ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />;
        // 1, 2, 3: Mainly clear, partly cloudy, and overcast
        if ([1, 2, 3].includes(code)) return isDay ? <CloudSun className="w-3 h-3" /> : <CloudMoon className="w-3 h-3" />;
        // 45, 48: Fog
        if ([45, 48].includes(code)) return <CloudFog className="w-3 h-3" />;
        // 51, 53, 55: Drizzle
        if ([51, 53, 55].includes(code)) return <CloudDrizzle className="w-3 h-3" />;
        // 61, 63, 65: Rain
        if ([61, 63, 65, 80, 81, 82].includes(code)) return <CloudRain className="w-3 h-3" />;
        // 71, 73, 75: Snow
        if ([71, 73, 75, 77, 85, 86].includes(code)) return <CloudSnow className="w-3 h-3" />;
        // 95, 96, 99: Thunderstorm
        if ([95, 96, 99].includes(code)) return <CloudLightning className="w-3 h-3" />;

        return isDay ? <CloudSun className="w-3 h-3" /> : <CloudMoon className="w-3 h-3" />;
    };

    const isVoiceRequesting = voiceState === 'requesting';
    const isRecording = voiceState === 'recording';
    const isTranscribing = voiceState === 'transcribing';
    const isVoiceBusy = isVoiceRequesting || isRecording || isTranscribing;

    // Show loading screen until background image loads
    if (!imageLoaded) {
        return (
            <div className="fixed inset-0 w-full h-full bg-gray-900 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-white/50" />
            </div>
        );
    }

    return (
        <div className="fixed inset-0 w-full h-full bg-gray-900 text-white overflow-hidden font-sans">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;700&display=swap');
                
                .font-outfit { font-family: 'Outfit', sans-serif; }
                
                /* Glassmorphism Utilities */
                .glass-panel {
                    background: rgba(20, 20, 20, 0.6);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                .glass-card {
                    background: rgba(255, 255, 255, 0.08);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                }
                
                /* Animations */
                .slide-in-up { animation: slideInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; transform: translateY(20px); }
                @keyframes slideInUp {
                    to { opacity: 1; transform: translateY(0); }
                }

                .fade-in { animation: fadeIn 0.5s ease-out forwards; }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                /* Hide Scrollbar */
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                
                /* Image Carousel */
                .image-carousel-enter {
                    animation: carouselSlideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    opacity: 0;
                    transform: translateY(12px);
                }
                @keyframes carouselSlideIn {
                    to { opacity: 1; transform: translateY(0); }
                }
                .carousel-track {
                    display: flex;
                    overflow-x: auto;
                    scroll-snap-type: x mandatory;
                    scroll-behavior: smooth;
                    -webkit-overflow-scrolling: touch;
                    gap: 8px;
                }
                .carousel-track::-webkit-scrollbar { display: none; }
                .carousel-track { -ms-overflow-style: none; scrollbar-width: none; }
                .carousel-slide {
                    flex: 0 0 100%;
                    scroll-snap-align: center;
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                }
                .carousel-slide img {
                    width: 100%;
                    height: 180px;
                    object-fit: cover;
                    display: block;
                    transition: transform 0.4s ease;
                }
                .carousel-slide:hover img {
                    transform: scale(1.03);
                }
                .carousel-overlay {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 50%;
                    background: linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%);
                    pointer-events: none;
                }
                .carousel-nav-btn {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background: rgba(0,0,0,0.5);
                    backdrop-filter: blur(8px);
                    border: 1px solid rgba(255,255,255,0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    z-index: 10;
                }
                .carousel-nav-btn:hover {
                    background: rgba(0,0,0,0.7);
                    border-color: rgba(255,255,255,0.3);
                }
                .carousel-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.3);
                    transition: all 0.3s ease;
                }
                .carousel-dot-active {
                    background: white;
                    width: 16px;
                    border-radius: 3px;
                }
                .mask-image-gradient {
                    mask-image: linear-gradient(to top, transparent 0%, black 10%, black 100%);
                    -webkit-mask-image: linear-gradient(to top, transparent 0%, black 10%, black 100%);
                }

                .voice-recording-surface,
                .voice-status-surface {
                    height: 40px;
                    border-radius: 999px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    overflow: hidden;
                    background: transparent;
                    color: rgba(255, 255, 255, 0.88);
                }
                .voice-cancel-button {
                    width: 32px;
                    height: 32px;
                    border-radius: 999px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: background 0.2s ease, transform 0.2s ease;
                }
                .voice-recording-surface .voice-cancel-button:hover,
                .voice-status-surface .voice-cancel-button:hover {
                    background: rgba(255, 255, 255, 0.10);
                }
                .voice-cancel-button:active {
                    transform: scale(0.94);
                }
                .voice-wave-full {
                    flex: 1 1 auto;
                    min-width: 132px;
                    max-width: 190px;
                    height: 34px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    padding: 0 2px;
                    overflow: hidden;
                }
                .voice-wave-full span {
                    display: block;
                    flex: 0 0 3px;
                    width: 3px;
                    height: var(--bar-height, 10px);
                    min-height: 7px;
                    max-height: 34px;
                    border-radius: 999px;
                    background: rgba(255, 255, 255, 0.9);
                    opacity: var(--bar-opacity, 0.72);
                    transition: height 0.14s linear, opacity 0.14s linear;
                    transform-origin: center;
                }
                .voice-button-recording {
                    background: rgba(255, 255, 255, 0.10);
                    color: rgba(255, 255, 255, 0.9);
                    box-shadow: none;
                    animation: none;
                }
                @keyframes voicePulse {
                    0% { box-shadow: 0 0 0 0 rgba(244, 63, 94, 0.45); }
                    70% { box-shadow: 0 0 0 10px rgba(244, 63, 94, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(244, 63, 94, 0); }
                }

                /* Bot message link styling */
                .prose a {
                    color: #60a5fa;
                    text-decoration: underline;
                    text-underline-offset: 2px;
                    transition: color 0.2s ease;
                }
                .prose a:hover {
                    color: #93bbfd;
                }
            `}</style>

            {/* BACKGROUND LAYER */}
            <div className="absolute inset-0 z-0 fade-in">
                <img
                    src={BACKGROUND_IMAGE_URL}
                    className="w-full h-full object-cover opacity-60"
                    alt="Luxury Room"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/90"></div>
            </div>

            {/* MAIN APP CONTAINER */}
            <div className="relative z-10 h-full flex flex-col justify-between pt-6 pb-6 px-4 font-outfit">

                {/* 1. TOP BAR (Status) */}
                <div className="flex justify-between items-center mb-4 px-2">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onBack}
                                className="p-1 -ml-2 hover:bg-white/10 rounded-full transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5 text-white" />
                            </button>
                            <h1 className="text-2xl font-bold tracking-tight">
                                {getGreeting()}{guestName ? `, ${guestName}` : ''}
                            </h1>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-300 pl-6">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                            <span>Concierge Active</span>
                            <span className="mx-1">•</span>
                            {weather ? (
                                <>
                                    {getWeatherIcon(weather.weather_code, weather.is_day)}
                                    <span>{Math.round(weather.temperature_2m)}°C | {Math.round((weather.temperature_2m * 9 / 5) + 32)}°F</span>
                                </>
                            ) : (
                                <>
                                    <CloudMoon className="w-3 h-3" />
                                    <span>...</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/10">
                        {hotelLogoPath ? (
                            <img src={hotelLogoPath} alt="Logo" className="w-full h-full rounded-full object-cover" />
                        ) : (
                            <User className="w-4 h-4 text-white" />
                        )}
                    </div>
                </div>

                {/* 2. WIDGET STREAM (The "Feed") */}
                <div
                    ref={scrollAreaRef}
                    className="flex-1 overflow-y-auto no-scrollbar space-y-4 pb-4 mask-image-gradient"
                    onScroll={handleScroll}
                >
                    {/* History loading indicator */}
                    {isLoadingHistory && (
                        <div className="flex justify-center py-3">
                            <Loader2 className="w-5 h-5 animate-spin text-white/40" />
                        </div>
                    )}
                    {messages.map((msg, idx) => {
                        // Refined Fix: Hide empty bot message during initial streaming phase
                        const isLatestMessage = idx === messages.length - 1;
                        const isEmptyStreamingBot = loading && isLatestMessage && msg.role === 'bot' && !msg.content;
                        if (isEmptyStreamingBot) return null;

                        const messageKey = msg.msgId || `${msg.role}-${idx}-${msg.created_on}`;

                        return (
                            <div key={messageKey} className={msg.isHistory ? "" : "slide-in-up"}>
                                {msg.role === 'user' ? (
                                    /* User Message */
                                    <div className="flex justify-end mb-2">
                                        <div className="bg-gray-700 text-white text-sm px-4 py-2 max-w-[85%] rounded-xl rounded-tr-sm shadow-lg">
                                            {msg.content}
                                        </div>
                                    </div>
                                ) : (
                                    /* Bot Message - General Card Style */
                                    <div className="flex justify-start items-start mb-6 gap-3">
                                        <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center mt-1 flex-shrink-0 overflow-hidden">
                                            {hotelLogoPath ? (
                                                <img src={hotelLogoPath} alt="Bot" className="w-full h-full object-cover" />
                                            ) : (
                                                <Sparkles className="w-4 h-4 text-white" />
                                            )}
                                        </div>
                                        <div className="flex flex-col max-w-[85%]">
                                            <div className="glass-card rounded-2xl p-4">
                                                <div
                                                    className="text-sm text-gray-100 prose prose-invert max-w-none"
                                                    dangerouslySetInnerHTML={{ __html: msg.html || msg.content }}
                                                />
                                            </div>
                                            {/* Premium Image Carousel — revealed when done event arrives */}
                                            {msg.showImages && msg.images && msg.images.length > 0 && (
                                                <ImageCarousel images={msg.images} />
                                            )}
                                            {msg.messageId && (
                                                <div className="flex gap-2 mt-2 ml-1">
                                                    <button
                                                        onClick={() => onSendFeedback(msg.messageId, 1)}
                                                        className={`${msg.feedback === 1 ? 'text-green-400' : 'text-gray-400'} hover:text-green-400 transition p-1`}
                                                        title="Helpful"
                                                    >
                                                        <ThumbsUp className={`w-4 h-4 ${msg.feedback === 1 ? 'fill-green-400/20' : ''}`} strokeWidth={1.5} />
                                                    </button>
                                                    <button
                                                        onClick={() => onSendFeedback(msg.messageId, 0)}
                                                        className={`${msg.feedback === 0 ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 transition p-1`}
                                                        title="Not helpful"
                                                    >
                                                        <ThumbsDown className={`w-4 h-4 ${msg.feedback === 0 ? 'fill-red-400/20' : ''}`} strokeWidth={1.5} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Loading Indicator */}
                    {loading && (loadingPhase === 'thinking' || loadingPhase === 'typing') && (
                        (() => {
                            const lastMessage = messages[messages.length - 1];
                            const isBotStreaming = lastMessage && lastMessage.role === 'bot' && lastMessage.content !== '';
                            if (isBotStreaming) return null;

                            return (
                                <div className="flex justify-start mb-4 px-1 slide-in-up">
                                    <div className="glass-card rounded-2xl px-3 py-2 flex items-center space-x-2 text-xs text-gray-300">
                                        <Loader2 className="w-3 h-3 animate-spin text-emerald-400" />
                                        {loadingPhase === 'typing' && (
                                            <span>
                                                <Typewriter text="Concierge is thinking..." />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })()
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* 3. INTERACTION DOCK (Bottom) */}
                <div className="w-full flex flex-col space-y-3">

                    {/* Quick Chips Rail */}
                    {suggestions && suggestions.length > 0 && (
                        <div className="w-full overflow-x-auto no-scrollbar py-2 px-1">
                            <div className="flex space-x-3 w-max">
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion.suggestion_text)}
                                        disabled={loading}
                                        className="flex-shrink-0 bg-white/10 hover:bg-white/20 border border-white/5 rounded-full px-4 py-2 text-sm backdrop-blur-sm transition disabled:opacity-50 whitespace-nowrap"
                                    >
                                        {suggestion.suggestion_text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {voiceError && (
                        <div className="px-4 text-xs text-rose-200/90">
                            {voiceError}
                        </div>
                    )}

                    {/* The Command Bar */}
                    <div className="glass-panel rounded-[2rem] p-1.5 flex items-center shadow-2xl gap-1">

                        {/* Text Input */}
                        <div className="flex-1 min-w-0">
                            {isVoiceBusy ? (
                                <div className={isRecording ? 'voice-recording-surface pl-1 pr-3' : 'voice-status-surface pl-1 pr-3'}>
                                    <button
                                        type="button"
                                        onClick={cancelVoiceInteraction}
                                        className="voice-cancel-button"
                                        aria-label="Cancel voice recording"
                                        title="Cancel voice recording"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                    {isRecording ? (
                                        <>
                                            <div className="voice-wave-full" aria-hidden="true">
                                                {voiceLevels.map((height, index) => (
                                                    <span
                                                        key={index}
                                                        style={{
                                                            '--bar-height': `${height}px`,
                                                            '--bar-opacity': Math.min(1, 0.38 + (height / VOICE_WAVE_MAX_HEIGHT) * 0.56)
                                                        }}
                                                    />
                                                ))}
                                            </div>

                                        </>
                                    ) : (
                                        <div className="flex flex-1 min-w-0 items-center justify-center gap-2 text-sm">
                                            <Loader2 className="w-4 h-4 animate-spin text-emerald-300" />
                                            <span className="truncate">{isVoiceRequesting ? 'Opening microphone...' : 'Transcribing...'}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    placeholder="Ask Concierge anything..."
                                    className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 px-4 text-base"
                                    disabled={loading}
                                />
                            )}
                        </div>

                        {/* Voice Button */}
                        <button
                            type="button"
                            onClick={handleVoiceButtonClick}
                            disabled={loading || isVoiceRequesting || isTranscribing}
                            aria-label={isRecording ? 'Stop voice recording' : 'Start voice recording'}
                            title={isRecording ? 'Stop voice recording' : 'Start voice recording'}
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition flex-shrink-0 ${isRecording ? 'voice-button-recording' : 'bg-white/10 hover:bg-white/20'} ${(loading || isVoiceRequesting || isTranscribing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isVoiceRequesting || isTranscribing ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                isRecording ? <Square className="w-4 h-4 fill-current" /> : <Mic className="w-5 h-5" />
                            )}
                        </button>

                        {/* Send Button */}
                        <button
                            onClick={handleSend}
                            disabled={!input.trim() || loading || isVoiceBusy}
                            className={`w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white transition flex-shrink-0 ${(!input.trim() || loading || isVoiceBusy) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/20'}`}
                        >
                            <ArrowUp className="w-5 h-5" />
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}


